import React, { useEffect, useState } from 'react';
import {
    Box, Button, TextField, Typography, Paper, List, ListItem, ListItemText, CircularProgress
} from '@mui/material';
import { useTour } from '../../../UserGuide/TourProvider';
import ConsentForm from '../../ConsentForm/ConsentForm';
import { useTranslation } from 'react-i18next';

const SurveyIntroduction = ({ user, onUpdate, onNext }) => {
    const [editMode, setEditMode] = useState(false);
    const [currentDateTime, setCurrentDateTime] = useState(new Date());
    const { startPageTour } = useTour();
    const [hasStartedTour, setHasStartedTour] = useState(false);
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        place: '',
        street: '',
        telephone: ''
    });
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [formValid, setFormValid] = useState(false);
    const [consentOpen, setConsentOpen] = useState(false);
    const [hasAgreedToConsent, setHasAgreedToConsent] = useState(false);

    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.firstname || '',
                lastName: user.lastname || '',
                email: user.email || '',
                place: user.place || '',
                street: user.street || '',
                telephone: user.telephone || ''
            });
            
            // Check if user has agreed to consent
            const consentKey = `consent_agreed_${user._id}`;
            const hasConsented = localStorage.getItem(consentKey) === 'true';
            if (!hasConsented) {
                setConsentOpen(true);
            } else {
                setHasAgreedToConsent(true);
            }
            
            const needsProfile = !user.firstname || !user.lastname;
            if (needsProfile && !hasStartedTour && !user.hasSeenSurveyTour && hasConsented) {
                setTimeout(() => {
                    if (startPageTour) {
                        startPageTour('surveyIntroduction');
                        setHasStartedTour(true);
                    }
                }, 1500);
            }
        }
    }, [user, hasStartedTour, startPageTour]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setCurrentDateTime(new Date());
        }, 1000); // Update every second

        return () => clearInterval(intervalId); // Cleanup interval on component unmount
    }, []);

    useEffect(() => {
        validateForm();
    }, [formData]);

    const formatDate = (date) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };
        return date.toLocaleDateString(undefined, options);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevFormData => ({
            ...prevFormData,
            [name]: value,
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.firstName) newErrors.firstName = t('validation.firstNameRequired');
        if (!formData.lastName) newErrors.lastName = t('validation.lastNameRequired');
        if (!formData.email) newErrors.email = t('validation.emailRequired');
        // Place, street, and telephone are now optional
        setErrors(newErrors);
        setFormValid(Object.keys(newErrors).length === 0);
    };

    const handleConsentAgree = () => {
        const consentKey = `consent_agreed_${user._id}`;
        localStorage.setItem(consentKey, 'true');
        setHasAgreedToConsent(true);
        setConsentOpen(false);
    };

    const handleConsentClose = () => {
        setConsentOpen(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (formValid && hasAgreedToConsent) {
            const updateFormData = new FormData();
            Object.keys(formData).forEach(key => {
                if (formData[key]) {
                    updateFormData.append(key, formData[key]);
                }
            });

            console.log('Form data being submitted:', formData);

            try {
                setIsSaving(true); // Start saving
                await onUpdate(updateFormData);
                alert(t('alerts.profileUpdated'));
                setEditMode(false); // Exit edit mode after updating
                setIsSaving(false); // Stop saving
                // Remove automatic navigation - let user click Next button manually
                // onNext(); // Removed - user should stay on same page
            } catch (error) {
                setIsSaving(false); // Stop saving on error
                console.error('Failed to update profile:', error);
                alert(t('alerts.profileUpdateFailed'));
            }
        } else if (!hasAgreedToConsent) {
            setConsentOpen(true);
        } else {
            console.log('Form validation failed:', errors);
        }
    };

    return (
        <Box sx={{ padding: 4, textAlign: 'center' }}>
            <Typography variant="h3" gutterBottom>{t('surveyInstructions.title')}</Typography>
            <Typography variant="subtitle1" gutterBottom>{t('surveyInstructions.date')}: {formatDate(currentDateTime)}</Typography>
            <Paper elevation={3} sx={{ padding: 4, marginTop: 2 }}>
                {editMode ? (
                    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-tour="edit-form">
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, textAlign: 'left' }}>
                            {t('forms.requiredFieldsMessage', '* First Name and Last Name are required. Place, Street, and Telephone are optional.')}
                        </Typography>
                        <TextField
                            label={t('forms.firstName') + ' *'}
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                            data-tour="firstname-field"
                        />
                        <TextField
                            label={t('forms.lastName') + ' *'}
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                        />
                        <TextField
                            label={t('forms.place') + ' ' + t('forms.optional', '(optional)')}
                            name="place"
                            value={formData.place}
                            onChange={handleInputChange}
                            error={!!errors.place}
                            helperText={errors.place}
                        />
                        <TextField
                            label={t('forms.street') + ' ' + t('forms.optional', '(optional)')}
                            name="street"
                            value={formData.street}
                            onChange={handleInputChange}
                            error={!!errors.street}
                            helperText={errors.street}
                        />
                        <TextField
                            label={t('forms.telephone') + ' ' + t('forms.optional', '(optional)')}
                            name="telephone"
                            value={formData.telephone}
                            onChange={handleInputChange}
                            error={!!errors.telephone}
                            helperText={errors.telephone}
                        />
                        <TextField
                            label={t('forms.email')}
                            name="email"
                            value={formData.email}
                            error={!!errors.email}
                            helperText={errors.email}
                            type="email"
                            disabled
                        />
                        <Button type="submit" variant="contained" color="primary" disabled={isSaving} data-tour="save-button">
                            {isSaving ? <CircularProgress size={24} /> : t('forms.save')}
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'left' }}>
                        <Typography>{t('forms.firstName')}: {user.firstname || t('forms.notProvided')}</Typography>
                        <Typography>{t('forms.lastName')}: {user.lastname || t('forms.notProvided')}</Typography>
                        <Typography>{t('forms.email')}: {user.email || t('forms.notProvided')}</Typography>
                        <Typography>{t('forms.place')}: {user.place || t('forms.notProvided')}</Typography>
                        <Typography>{t('forms.street')}: {user.street || t('forms.notProvided')}</Typography>
                        <Typography>{t('forms.telephone')}: {user.telephone || t('forms.notProvided')}</Typography>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => setEditMode(true)} 
                            sx={{ marginTop: 2 }}
                            data-tour="edit-button"
                        >
                            {t('forms.edit')}
                        </Button>
                    </Box>
                )}

            </Paper>
            <Box sx={{ marginTop: 4 }}>
                <Paper elevation={3} sx={{ padding: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">{t('surveyInstructions.instructions')}</Typography>
                        <Button 
                            variant="outlined" 
                            size="small"
                            onClick={() => setConsentOpen(true)}
                        >
                            {t('surveyInstructions.viewConsentForm')}
                        </Button>
                    </Box>
                    <List>
                        <ListItem>
                            <ListItemText primary={t('surveyInstructions.instruction1')} />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary={t('surveyInstructions.instruction2')} />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary={t('surveyInstructions.instruction3')} />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary={t('surveyInstructions.instruction4')} />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary={t('surveyInstructions.instruction5')} />
                        </ListItem>
                    </List>
                    <Button 
                        variant="contained" 
                        color="secondary" 
                        onClick={() => {
                            if (!hasAgreedToConsent) {
                                setConsentOpen(true);
                            } else if (formValid) {
                                onNext();
                            }
                        }} 
                        disabled={!formValid || editMode || isSaving || !hasAgreedToConsent} 
                        sx={{ marginTop: 2 }}
                        data-tour="next-button"
                    >
                        {t('forms.next')}
                    </Button>
                    {!hasAgreedToConsent && (
                        <Typography 
                            variant="caption" 
                            color="error" 
                            sx={{ display: 'block', mt: 1 }}
                        >
                            {t('surveyInstructions.consentNotAgreed')}
                        </Typography>
                    )}
                </Paper>
            </Box>
            
            <ConsentForm 
                open={consentOpen}
                onClose={handleConsentClose}
                onAgree={handleConsentAgree}
            />
        </Box>
    );
};

export default SurveyIntroduction;
